import React, { useState, useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { publicAPI } from '../lib/api';
import { useSeo } from '../lib/seoHelpers';
import { Calendar, Clock, ArrowLeft, Loader2 } from 'lucide-react';

const formatDate = (iso, language) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(language === 'en' ? 'en-GB' : 'nl-BE',
      { day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return String(iso).slice(0, 10); }
};

export const BlogPostPage = () => {
  const { lang, slug } = useParams();
  const language = lang === 'en' ? 'en' : 'nl';
  const [post, setPost] = useState(null);
  const [translations, setTranslations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    publicAPI.getBlogPost(language, slug)
      .then(({ data }) => {
        if (cancelled) return;
        setPost(data.post);
        setTranslations(data.translations || []);
      })
      .catch(() => { if (!cancelled) setNotFound(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [language, slug]);

  useSeo({
    title: post ? `${post.title} — FoodFlows` : 'FoodFlows Blog',
    description: post?.meta_description || post?.excerpt || '',
    image: post?.featured_image || 'https://food-flows.com/og-image.png',
    canonical: `https://food-flows.com/blog/${language}/${slug}`,
  });

  if (notFound) return <Navigate to="/blog" replace />;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
      </div>
    );
  }
  if (!post) return null;

  const backTo = language === 'nl' ? '/blog' : `/blog/${language}`;

  return (
    <div className="min-h-screen bg-white" data-testid="blog-post-page">
      <header className="border-b border-gray-200/80 bg-white/80 backdrop-blur sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-9 h-9 rounded-lg bg-[#1A4D2E] flex items-center justify-center text-white font-bold">F</div>
            <span className="font-heading text-lg font-bold text-gray-900 group-hover:text-[#1A4D2E] transition-colors">FoodFlows</span>
          </Link>
          <Link to="/register" className="px-4 py-2 rounded-full bg-[#1A4D2E] text-white text-sm font-medium hover:bg-[#143d24] transition-colors">
            {language === 'en' ? 'Get started' : 'Start gratis'}
          </Link>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-6 py-12">
        <Link to={backTo} className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-[#1A4D2E] mb-6" data-testid="blog-back-link">
          <ArrowLeft className="w-4 h-4" /> {language === 'en' ? 'Back to blog' : 'Terug naar de blog'}
        </Link>

        {post.category && (
          <span className="text-xs font-medium text-[#1A4D2E] bg-emerald-50 px-2 py-0.5 rounded-full">
            {post.category}
          </span>
        )}
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">{post.title}</h1>

        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mt-4">
          {post.published_at && (
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" /> {formatDate(post.published_at, language)}
            </span>
          )}
          <span className="flex items-center gap-1">
            <Clock className="w-4 h-4" /> {post.reading_minutes} {language === 'en' ? 'min read' : 'min leestijd'}
          </span>
          {translations.length > 0 && (
            <span className="flex items-center gap-2">
              {translations.map(t => (
                <Link key={t.language} to={`/blog/${t.language}/${t.slug}`}
                      className="underline hover:text-[#1A4D2E]"
                      data-testid={`blog-translation-${t.language}`}>
                  {t.language === 'en' ? 'Read in English' : 'Lees in het Nederlands'}
                </Link>
              ))}
            </span>
          )}
        </div>

        {post.featured_image && (
          <img src={post.featured_image} alt={post.title}
               className="w-full rounded-2xl mt-8 object-cover max-h-96" />
        )}

        {/* De HTML is server-side uit markdown gerenderd met html=false, dus ruwe
            HTML in de brontekst is al geëscapet. Zo zien crawler en bezoeker
            exact dezelfde opmaak, zonder markdown-library in de frontend. */}
        <div
          className="blog-content mt-8"
          dangerouslySetInnerHTML={{ __html: post.content_html }}
          data-testid="blog-content"
        />

        <div className="mt-12 p-6 bg-emerald-50 rounded-2xl border border-emerald-100">
          <h2 className="font-heading text-xl font-bold text-gray-900">
            {language === 'en' ? 'Start taking online orders' : 'Begin met online bestellingen'}
          </h2>
          <p className="text-gray-600 mt-2">
            {language === 'en'
              ? 'Your own ordering site, on your own domain, without commission per order.'
              : 'Je eigen bestelsite, op je eigen domein, zonder commissie per bestelling.'}
          </p>
          <Link to="/register" className="inline-block mt-4 px-5 py-2.5 rounded-full bg-[#1A4D2E] text-white font-medium hover:bg-[#143d24] transition-colors">
            {language === 'en' ? 'Get started' : 'Start gratis'}
          </Link>
        </div>
      </article>
    </div>
  );
};
